import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  type LinkProps,
  type NavLinkProps,
} from "react-router-dom";

// Lightweight in-app navigation guard. A component registers a function that
// returns `true` to allow nav (no unsaved work) or `false` to block.
// Navigation-initiating elements (breadcrumb links, header links, etc.) call
// `confirmNav()` before navigating. If any registered guard returns false,
// navigation is cancelled.
//
// This is a click-time guard - browser back/forward + tab close are covered
// separately via `beforeunload` listeners.

type Guard = () => boolean;
type GuardId = number;

type NavGuardContextValue = {
  register: (guard: Guard) => GuardId;
  unregister: (id: GuardId) => void;
  confirmNav: () => boolean;
};

const NavGuardContext = createContext<NavGuardContextValue | undefined>(undefined);

export function NavGuardProvider({ children }: { children: ReactNode }) {
  const nextIdRef = useRef(0);
  const guardsRef = useRef<Map<GuardId, Guard>>(new Map());

  const register = useCallback((guard: Guard) => {
    const id = ++nextIdRef.current;
    guardsRef.current.set(id, guard);
    return id;
  }, []);

  const unregister = useCallback((id: GuardId) => {
    guardsRef.current.delete(id);
  }, []);

  const confirmNav = useCallback(() => {
    for (const guard of guardsRef.current.values()) {
      if (!guard()) return false;
    }
    return true;
  }, []);

  const value = useMemo(
    () => ({ register, unregister, confirmNav }),
    [register, unregister, confirmNav]
  );

  return (
    <NavGuardContext.Provider value={value}>
      {children}
    </NavGuardContext.Provider>
  );
}

export function useNavGuard(): NavGuardContextValue {
  const ctx = useContext(NavGuardContext);
  if (!ctx) {
    // Outside a provider - everything is allowed.
    return {
      register: () => 0,
      unregister: () => {},
      confirmNav: () => true,
    };
  }
  return ctx;
}

// Drop-in <Link> replacement that runs any registered nav guards before
// navigating. If a guard returns false, the click is prevented.
export function GuardedLink({
  onClick,
  ...rest
}: LinkProps) {
  const { confirmNav } = useNavGuard();
  return (
    <Link
      {...rest}
      onClick={(e) => {
        if (!confirmNav()) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
    />
  );
}

// NavLink variant with the same guard behavior.
export function GuardedNavLink({
  onClick,
  ...rest
}: NavLinkProps) {
  const { confirmNav } = useNavGuard();
  return (
    <NavLink
      {...rest}
      onClick={(e) => {
        if (!confirmNav()) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
    />
  );
}

const NAV_GUARD_SENTINEL = "__nifusion_nav_guard__";

// Intercept browser back/forward while `isDirty` is true. Strategy: push a
// duplicate history entry (a "sentinel") so a back press fires popstate
// without actually navigating away. Then confirm. If denied, re-push the
// sentinel. If allowed, navigate back through it.
//
// Cleanup is the tricky part: the sentinel is identified by a marker on
// `history.state`, so on unmount/save we can detect and pop it without
// guessing.
export function useBrowserBackGuard(
  isDirty: boolean,
  message = "Discard unsaved changes and leave?"
) {
  useEffect(() => {
    if (!isDirty) return;

    function pushSentinel() {
      const merged = {
        ...(window.history.state ?? {}),
        [NAV_GUARD_SENTINEL]: true,
      };
      window.history.pushState(merged, "", window.location.href);
    }

    pushSentinel();
    let consuming = false;

    function onPopState() {
      if (consuming) {
        consuming = false;
        return;
      }
      // popstate fires AFTER history pops. We were at sentinel; now at the
      // original entry (URL unchanged because they're duplicates).
      if (window.confirm(message)) {
        // User leaves. Go back one more entry to honor their intent.
        consuming = true;
        window.history.back();
      } else {
        // User stays. Re-arm the sentinel.
        pushSentinel();
      }
    }

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // If we're still sitting at our sentinel (e.g. user just saved, no
      // navigation happened), consume it so a future back press works on
      // the first try.
      if (window.history.state?.[NAV_GUARD_SENTINEL]) {
        consuming = true;
        window.history.back();
      }
    };
  }, [isDirty, message]);
}

// Convenience hook: register `guard` while `enabled` is true. The guard runs
// at navigation time, so capture state with refs if it changes.
export function useRegisterNavGuard(enabled: boolean, guard: Guard) {
  const { register, unregister } = useNavGuard();
  const guardRef = useRef(guard);
  useEffect(() => {
    guardRef.current = guard;
  }, [guard]);
  useEffect(() => {
    if (!enabled) return;
    const id = register(() => guardRef.current());
    return () => unregister(id);
  }, [enabled, register, unregister]);
}
